import {useIntl} from 'react-intl'
import {REGEX} from '../../constants'
import {useXSSProtection} from './../../hooks/use-xss-protection'
import {useToast} from '../../hooks/use-toast'

/**
 * Custom React hook that generates and returns form field configurations for an outlet subscription form.
 *
 * This hook provides configuration objects for the "email" and "consent" fields, including their names, labels,
 * placeholders, default values, types, validation rules, error handling, and form control integration.
 * It leverages internationalization for labels and error messages, XSS protection for field validation,
 * and toast notifications for user feedback.
 *
 * @function
 * @param {Object} params - The parameters object.
 * @param {Object} params.form - The form object containing control and formState.
 * @param {Object} params.form.control - The control object for form management.
 * @param {Object} params.form.formState - The form state object.
 * @param {Object} params.form.formState.errors - The errors object containing validation errors.
 * @returns {Object} fields - An object containing configuration for the "email" and "consent" fields.
 * @returns {Object} fields.email - Configuration for the email input field, including validation and error handling.
 * @returns {Object} fields.consent - Configuration for the consent checkbox, including validation and error handling.
 *
 * @example
 * const fields = useOutletSubscriptionFields({ form });
 * <Controller {...fields.email} />
 * <Controller {...fields.consent} />
 */
export default function useOutletSubscriptionFields({
    form: {
        control,
        formState: {errors}
    }
}) {
    const toast = useToast()
    const {formatMessage} = useIntl()
    const {validateField} = useXSSProtection()

    const fields = {
        email: {
            name: 'outletsubscriptionemail',
            label: formatMessage({
                defaultMessage: 'Email Address',
                id: 'use_newsletter_fields.label.email'
            }),
            placeholder: 'you@email.com',
            defaultValue: '',
            type: 'email',
            autoComplete: 'email',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your email address.',
                    id: 'use_newsletter_fields.error.required_email'
                }),
                pattern: {
                    value: REGEX.EMAIL,
                    message: formatMessage({
                        defaultMessage: 'Please enter a valid email address.',
                        id: 'use_newsletter_fields.error.invalid_email'
                    })
                },
                validate: (value) =>
                    validateField(toast, value, 'outlet-subscription-form', 'email')
            },
            error: errors?.['outletsubscriptionemail'],
            control
        },
        consent: {
            name: 'outletsubscriptionconsent',
            label: formatMessage({
                defaultMessage: `Yes! I would like to receive update about products, promotions special offers news and events from Vilebrequin through the communication channels I provided`,
                id: 'outlet_subscription.form.checkbox.consent'
            }),
            defaultValue: false,
            type: 'checkbox',
            autoComplete: 'honorific-prefix',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please consent to continue.',
                    id: 'outlet_subscription.error.required_consent'
                })
            },
            error: errors?.['outletsubscriptionconsent'],
            control
        }
    }
    return fields
}
